import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  type RouterHistory,
} from '@tanstack/react-router';
import { lazy, Suspense } from 'react';
import type { ClientListFilter } from '~/api/types';
import { AppLayout } from './AppLayout';

/**
 * Each screen is its own chunk, loaded the first time someone opens it. Named exports become
 * React.lazy defaults here so the route files stay as they are.
 */
const ClientGeneralPage = lazy(() =>
  import('~/routes/ClientGeneral').then((m) => ({ default: m.ClientGeneralPage })),
);
const ClientsPage = lazy(() =>
  import('~/routes/Clients').then((m) => ({ default: m.ClientsPage })),
);
const ClientYearPage = lazy(() =>
  import('~/routes/ClientYear').then((m) => ({ default: m.ClientYearPage })),
);
const ComponentsPage = lazy(() =>
  import('~/routes/Components').then((m) => ({ default: m.ComponentsPage })),
);
const ConversationPage = lazy(() =>
  import('~/routes/Conversation').then((m) => ({ default: m.ConversationPage })),
);
const HomePage = lazy(() => import('~/routes/Home').then((m) => ({ default: m.HomePage })));
const NotFoundPage = lazy(() =>
  import('~/routes/NotFound').then((m) => ({ default: m.NotFoundPage })),
);
const SettingsPage = lazy(() =>
  import('~/routes/Settings').then((m) => ({ default: m.SettingsPage })),
);
const SetupPage = lazy(() => import('~/routes/Setup').then((m) => ({ default: m.SetupPage })));
const SignInPage = lazy(() => import('~/routes/SignIn').then((m) => ({ default: m.SignInPage })));
const StatesPage = lazy(() => import('~/routes/States').then((m) => ({ default: m.StatesPage })));
const UsagePage = lazy(() => import('~/routes/Usage').then((m) => ({ default: m.UsagePage })));
const WorkpaperPage = lazy(() =>
  import('~/routes/Workpaper').then((m) => ({ default: m.WorkpaperPage })),
);

/** While a screen's chunk loads: the page ground only, so nothing flashes. */
export function ScreenLoading() {
  return <div role="status" aria-label="Opening" className="min-h-screen bg-paper" />;
}

function Root() {
  return (
    <Suspense fallback={<ScreenLoading />}>
      <Outlet />
    </Suspense>
  );
}

/** Search params typed into the address bar arrive parsed ("1040" as a number); read them as text. */
const str = (v: unknown): string | undefined =>
  typeof v === 'number' ? String(v) : typeof v === 'string' && v !== '' ? v : undefined;
const bool = (v: unknown): boolean => v === true || v === 'true' || v === '1';

const root = createRootRoute({ component: Root, notFoundComponent: () => <NotFoundPage /> });

const signIn = createRoute({
  getParentRoute: () => root,
  path: 'sign-in',
  validateSearch: (s: Record<string, unknown>) => ({
    error: str(s.error),
    returnTo: str(s.returnTo),
  }),
  component: function SignInRoute() {
    const s = signIn.useSearch();
    return <SignInPage error={s.error ?? null} returnTo={s.returnTo ?? null} />;
  },
});

const setup = createRoute({
  getParentRoute: () => root,
  path: 'setup',
  component: () => <SetupPage />,
});

const app = createRoute({ getParentRoute: () => root, id: 'app', component: AppLayout });

const home = createRoute({
  getParentRoute: () => app,
  path: '/',
  component: () => <HomePage view="home" />,
});

const intake = createRoute({
  getParentRoute: () => app,
  path: 'intake',
  validateSearch: (s: Record<string, unknown>) => ({ showDropOverlay: bool(s.showDropOverlay) }),
  component: function IntakeRoute() {
    return <HomePage view="intake" forceOverlay={intake.useSearch().showDropOverlay} />;
  },
});

const FILTERS: ClientListFilter[] = ['all', '1041', 'gaps', 'mine', 'review'];
const clients = createRoute({
  getParentRoute: () => app,
  path: 'clients',
  validateSearch: (s: Record<string, unknown>) => ({
    filter: (FILTERS.includes(str(s.filter) as ClientListFilter)
      ? str(s.filter)
      : 'all') as ClientListFilter,
    q: str(s.q),
  }),
  component: function ClientsRoute() {
    const s = clients.useSearch();
    return <ClientsPage filter={s.filter} query={s.q ?? ''} />;
  },
});

const client = createRoute({
  getParentRoute: () => app,
  path: 'clients/$clientId',
  validateSearch: (s: Record<string, unknown>) => ({ wide: bool(s.wide) }),
  component: function ClientRoute() {
    const { clientId } = client.useParams();
    return <ClientGeneralPage clientId={clientId} wide={client.useSearch().wide} />;
  },
});

const year = createRoute({
  getParentRoute: () => app,
  path: 'clients/$clientId/years/$year',
  validateSearch: (s: Record<string, unknown>) => ({
    type: str(s.type),
    thread: s.thread === 'private' ? 'private' : 'team',
  }),
  component: function YearRoute() {
    const p = year.useParams();
    const s = year.useSearch();
    return (
      <ClientYearPage
        clientId={p.clientId}
        year={Number(p.year)}
        returnType={s.type ?? null}
        thread={s.thread as 'team' | 'private'}
      />
    );
  },
});

const deliverable = createRoute({
  getParentRoute: () => app,
  path: 'deliverables/$deliverableId',
  validateSearch: (s: Record<string, unknown>) => ({
    v: typeof s.v === 'number' ? s.v : s.v ? Number(s.v) : undefined,
  }),
  component: function DeliverableRoute() {
    const { deliverableId } = deliverable.useParams();
    return (
      <WorkpaperPage deliverableId={deliverableId} version={deliverable.useSearch().v ?? null} />
    );
  },
});

const conversation = createRoute({
  getParentRoute: () => app,
  path: 'conversations/$conversationId',
  component: function ConversationRoute() {
    return <ConversationPage conversationId={conversation.useParams().conversationId} />;
  },
});

const usage = createRoute({
  getParentRoute: () => app,
  path: 'usage',
  validateSearch: (s: Record<string, unknown>) => ({ allowanceState: str(s.allowanceState) }),
  component: function UsageRoute() {
    return <UsagePage allowanceState={usage.useSearch().allowanceState ?? null} />;
  },
});

const settings = createRoute({
  getParentRoute: () => app,
  path: 'settings',
  component: SettingsPage,
});
const states = createRoute({
  getParentRoute: () => app,
  path: 'states',
  component: () => <StatesPage />,
});
const components = createRoute({
  getParentRoute: () => app,
  path: 'components',
  component: () => <ComponentsPage />,
});

const routeTree = root.addChildren([
  signIn,
  setup,
  app.addChildren([
    home,
    intake,
    clients,
    client,
    year,
    deliverable,
    conversation,
    usage,
    settings,
    states,
    components,
  ]),
]);

export function createAppRouter(history?: RouterHistory) {
  return createRouter({
    routeTree,
    defaultPreload: false,
    scrollRestoration: true,
    ...(history ? { history } : {}),
  });
}
